// src/domains/fees.ts —— U-23 费率说明页（「我的」→ 费率说明）纯视图构建：钱包 / 卡片通用 / 按卡产品分组的对客费用。
// 数据只来自 GET /public/config（sh.config）、GET /user/card-products 与只读报价 POST /user/wallet/deposit-quote，不写死任何数字。
// 单位口径（用户拍板 2026-10-15 / 2026-10-16）：钱包与各项费用写 USDT，卡交易金额（授权撤销门槛）写 USD；数字去尾零；为 0 的项显式写「免费」。
// 本模块无副作用、不持状态（加载与切片读写在 me.ts），便于单测。
import type { CardProduct, PublicConfig } from '../../../shared/src/api/types';
import { interpolate } from '../../../shared/src/errors';
import { authCancelFeeText } from '../../../shared/src/fees';
import { fmtPlain, mulRateHalfUp, toMicro, type Micro } from '../../../shared/src/money';
import { str } from './types';

type Dict = Readonly<Record<string, unknown>>;
type Lang = 'zh' | 'en';

export interface FeeLine {
  label: string;
  value: string;
  note: string;
  hasNote: boolean;
}

export interface FeeGroup {
  title: string;
  sub: string;
  hasSub: boolean;
  rows: FeeLine[];
}

// 只读报价中本页用到的字段（wallet.types CheckoutQuote 的子集；示例金额固定 100 USDT）
export interface FeeQuote {
  amount_usdt: string;
  applied_rate: string;
  fee_usd: string;
}

// 充值费示例金额（06 U-23：「充 100 USDT 收 x USDT」）
export const EXAMPLE_DEPOSIT = '100';
// 入金手续费精度：HALF_UP 到 0.001（D33 / 2026-10-15 拍板，不上取整）
const DEPOSIT_FEE_UNIT: Micro = 1_000n;
const RATE_CAP: Micro = 100_000_000n; // 100%

function micro(v: unknown): Micro | null {
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  try {
    return toMicro(String(v));
  } catch {
    return null;
  }
}

// 去尾零；非法 / 缺省返回 null（调用方显示「—」，绝不回落 0 冒充免费）
function plainOf(v: unknown): string | null {
  const m = micro(v);
  return m === null || m < 0n ? null : fmtPlain(m);
}

const line = (label: string, value: string, note = ''): FeeLine => ({ label, value, note, hasNote: note !== '' });

function usdt(t: Dict, v: unknown): string {
  const p = plainOf(v);
  if (p === null) return '—';
  return micro(v) === 0n ? str(t, 'feeFree') : `${p} USDT`;
}

function perTx(t: Dict, v: unknown): string {
  const p = plainOf(v);
  if (p === null) return '—';
  return micro(v) === 0n ? str(t, 'feeFree') : `${p} USDT${str(t, 'feesPerTx')}`;
}

function pctOnly(t: Dict, v: unknown): string {
  const p = plainOf(v);
  if (p === null) return '—';
  return micro(v) === 0n ? str(t, 'feeFree') : `${p}%`;
}

// 比例 + 每笔固定：皆 0 →「免费」；只有一项非 0 只写该项
function rateFixed(t: Dict, rate: unknown, fixed: unknown): string {
  const r = plainOf(rate);
  const f = plainOf(fixed);
  if (r === null || f === null) return '—';
  const hasR = micro(rate) !== 0n;
  const hasF = micro(fixed) !== 0n;
  if (!hasR && !hasF) return str(t, 'feeFree');
  const fixedText = `${f} USDT${str(t, 'feesPerTx')}`;
  if (!hasF) return `${r}%`;
  return hasR ? `${r}% + ${fixedText}` : fixedText;
}

// 充值费示例：优先只读报价（已含汇率差口径，fee 以入账额计）；无报价时按配置费率本地估算 HALF_UP 0.001
export function depositExample(rate: string | null, quote: FeeQuote | null): string | null {
  // 报价按 EXAMPLE_DEPOSIT 请求；金额不符（防御）或字段畸形时回落本地估算
  if (quote && micro(quote.amount_usdt) === toMicro(EXAMPLE_DEPOSIT)) {
    const fee = plainOf(quote.fee_usd);
    if (fee !== null) return fee;
  }
  if (rate === null) return null;
  const m = mulRateHalfUp(toMicro(EXAMPLE_DEPOSIT), rate);
  const rounded = ((m + DEPOSIT_FEE_UNIT / 2n) / DEPOSIT_FEE_UNIT) * DEPOSIT_FEE_UNIT;
  return fmtPlain(rounded);
}

// 入金手续费率取值：缺省 / 畸形 / 越界（< 0 或 ≥ 100）返回 null（页面显示「—」，绝不回落 0）
function rateValue(raw: unknown): string | null {
  const m = micro(raw);
  return m === null || m < 0n || m >= RATE_CAP ? null : String(raw);
}

// 平台入金手续费率（GET /public/config → fees.deposit_fee_rate；官网与未登录口径）
export function depositRateOf(cfg: PublicConfig | null): string | null {
  return rateValue(cfg?.fees.deposit_fee_rate);
}

// 0045 代理加价：登录用户的生效充值手续费率。own = GET /wallet（或 /wallet/fees.default）的 deposit_fee_rate，
// 按所属代理售价链解析，优先采用；仅在 own 缺省（旧后端 / 未取到：undefined / null / ''）时回落平台价 config.fees.deposit_fee_rate。
// own 存在但畸形 / 越界返回 null 显示「—」：不回落平台价，避免把低于实收的平台价展示给代理名下用户。
export function effectiveDepositRate(own: string | null | undefined, cfg: PublicConfig | null): string | null {
  if (own === undefined || own === null || own === '') return depositRateOf(cfg);
  const v = rateValue(own);
  if (v === null) console.error('[gp:fees] 用户生效 deposit_fee_rate 畸形或越界', own);
  return v;
}

const NET_FEE_KEY: Readonly<Record<string, string>> = { TRC20: 'fee_trc', ERC20: 'fee_erc', BEP20: 'fee_bep' };

// 钱包：充值手续费（+示例）、最低充值、兑换汇率、各可提网络提现手续费（比例 + 网络固定费；TRC20 未激活另收激活费）、最低提现
// ownRate：登录用户生效充值费率（GET /wallet → deposit_fee_rate），缺省时回落平台价（见 effectiveDepositRate）
export function walletGroup(t: Dict, cfg: PublicConfig | null, quote: FeeQuote | null, ownRate: string | null = null): FeeGroup {
  const rate = effectiveDepositRate(ownRate, cfg);
  const ex = depositExample(rate, quote);
  const rows: FeeLine[] = [
    line(str(t, 'feesDepFee'), rate === null ? '—' : pctOnly(t, rate), rate !== null && micro(rate) !== 0n && ex !== null ? interpolate(str(t, 'feesDepExample'), { amount: EXAMPLE_DEPOSIT, fee: ex }) : ''),
    line(str(t, 'minDeposit'), usdtAmount(cfg?.limits.min_deposit)),
    line(str(t, 'depRateLabel'), quote && plainOf(quote.applied_rate) !== null ? interpolate(str(t, 'depRateValue'), { rate: plainOf(quote.applied_rate) ?? '' }) : '—', quote ? str(t, 'feesFxNote') : ''),
  ];
  const wdRate = cfg?.fees.withdraw_fee_rate;
  const nets = cfg?.switches.withdraw_networks?.length ? cfg.switches.withdraw_networks : ['TRC20'];
  const act = micro(cfg?.limits.fee_trc_activation);
  for (const net of nets) {
    const key = NET_FEE_KEY[net];
    if (!key) continue;
    const note = net === 'TRC20' && act !== null && act > 0n ? interpolate(str(t, 'feesWdActNote'), { fee: fmtPlain(act) }) : '';
    rows.push(line(interpolate(str(t, 'feesWdFee'), { network: net }), wdFeeText(t, wdRate, cfg?.limits[key]), note));
  }
  rows.push(line(str(t, 'feesMinWithdraw'), usdtAmount(cfg?.limits.min_withdraw)));
  return { title: str(t, 'feesWalletTitle'), sub: '', hasSub: false, rows };
}

// 金额类（最低充值 / 最低提现）：0 也写「0 USDT」（不是费用，不写免费）
function usdtAmount(v: unknown): string {
  const p = plainOf(v);
  return p === null ? '—' : `${p} USDT`;
}

// 提现手续费 = 金额 × withdraw_fee_rate% + 网络固定费（F-06）
function wdFeeText(t: Dict, rate: unknown, fixed: unknown): string {
  const r = plainOf(rate);
  const f = plainOf(fixed);
  if (r === null || f === null) return '—';
  const hasR = micro(rate) !== 0n;
  const hasF = micro(fixed) !== 0n;
  if (!hasR && !hasF) return str(t, 'feeFree');
  if (!hasR) return `${f} USDT${str(t, 'feesPerTx')}`;
  return hasF ? `${r}% + ${f} USDT${str(t, 'feesPerTx')}` : `${r}%`;
}

// 卡片通用：增加额度免费（D33）、1 USDT = 1 USD 额度、卡转出固定费（全局 cardWithdrawFee）
export function cardCommonGroup(t: Dict, cfg: PublicConfig | null): FeeGroup {
  return {
    title: str(t, 'feesCardTitle'),
    sub: '',
    hasSub: false,
    rows: [
      line(str(t, 'feeAddLimit'), str(t, 'feeFree'), str(t, 'feesLimitRate')),
      line(str(t, 'feeCardWd'), perTx(t, cfg?.fees.card_withdraw_fee)),
    ],
  };
}

function productTitle(p: CardProduct, lang: Lang): string {
  const name = lang === 'en' ? p.name_en || p.name : p.name;
  return `${name} · ${p.bin}`;
}

// 按卡产品分组（可开卡产品，GET /user/card-products 已按可售过滤）：D14 对客费用，0 显式写免费；销卡能力关闭时不列销卡费
// 用户 2026-10-18：退款手续费不在费率说明展示（仍按产品 refund_fee_rate 收取）
export function productGroups(t: Dict, products: readonly CardProduct[], lang: Lang): FeeGroup[] {
  return products.map((p) => {
    const authCancel = authCancelFeeText(p, { fixed: str(t, 'authCancelFixed'), cond: str(t, 'authCancelCond') });
    const rows: FeeLine[] = [
      line(str(t, 'openFee'), usdt(t, p.open_fee)),
      ...(p.capabilities.close === false ? [] : [line(str(t, 'closeFee'), usdt(t, p.close_fee))]),
      line(str(t, 'txFee'), rateFixed(t, p.tx_fee_rate, p.tx_fee_fixed)),
      line(str(t, 'crossBorderFee'), rateFixed(t, p.cross_border_rate, p.cross_border_fixed)),
      line(str(t, 'declinedFee'), perTx(t, p.declined_fee)),
      line(str(t, 'authCancelFee'), authCancel || str(t, 'feeFree')),
      line(str(t, 'disputeFee'), perTx(t, p.dispute_fee)),
    ];
    return { title: productTitle(p, lang), sub: `${p.brand} · ${p.area}`, hasSub: true, rows };
  });
}

export const __test = { rateFixed, wdFeeText, usdt, perTx, pctOnly };
